import { useCallback, useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import type { PremiseConfig } from "@shared/types/foyer";
import type {
  ChainOfferFunnelRow,
  ChainOfferFunnelSummary,
  OfferFunnelRow,
  OfferFunnelSummary,
  OfferFunnelTotals,
} from "@shared/types/offerFunnel";
import { usePermission, type PlaceRef } from "../auth/access";
import { foyerApi } from "../http/foyer";
import { PageContainer, PageHeader } from "../components/PageHeader";
import { formatPercent } from "../components/engagementFormat";
import DemoPaymentsBanner from "../components/offers/DemoPaymentsBanner";
import OfferFunnelStrip, {
  FunnelLegend,
} from "../components/offers/OfferFunnelStrip";
import OfferRevenueTrend from "../components/offers/OfferRevenueTrend";
import {
  formatMoneyTotals,
  formatPerMille,
  moneyIsEmpty,
  perMilleHint,
  primaryMoney,
  revenueNote,
} from "../components/offers/offerRevenueFormat";
import { tokens } from "../theme";
import { useFormat } from "../i18n/format";

/**
 * Revenue — "this screen earned 42 300 kr" (`POS-12`).
 *
 * The page a salesperson opens after the guest demo and a hotel opens once a
 * month to decide whether to keep paying. One number leads; everything else
 * exists to make that number believable.
 *
 * ## The editorial rule, which is also the product
 *
 * **Revenue comes from accepted requests, never from impressions**
 * (`docs/room-offers.md` §5). An impression is not revenue. A request nobody
 * accepted has earned nothing. `pending` money is drawn as pipeline, in its own
 * figure with its own label, and is never added to the headline. A refund
 * reverses revenue by construction — the status changes and the sum drops —
 * so there is no adjustment row here and none should be invented.
 *
 * Where a number is a proxy it says so: `engagedRate` is a proxy for attention,
 * not a conversion rate, and the copy carries that sentence verbatim rather
 * than paraphrasing it into something softer.
 *
 * ## What the drop-offs are for
 *
 * The totals are not the interesting part. An offer with 4 000 impressions and
 * 2 requests is telling the hotel to change the picture or the price, and that
 * is the sentence the funnel strips exist to make legible. Revenue per 1000
 * impressions sits beside them so a 450 kr spa slot and a 95 kr breakfast can
 * be compared on one axis instead of by gut feel.
 *
 * ## Two layers, one page
 *
 * A location scoped → that hotel, with its day-by-day trend. A customer scoped
 * and NO location → every hotel of the chain, ranked best-earning first
 * (`CHAIN_CAPABLE_SECTIONS`). Care premises never appear: offers are hotel-only
 * for v1 (§10).
 */

const RANGES = [
  { value: 7, key: "offerRevenuePage.ranges.7" },
  { value: 30, key: "offerRevenuePage.ranges.30" },
  { value: 90, key: "offerRevenuePage.ranges.90" },
  { value: 365, key: "offerRevenuePage.ranges.365" },
] as const;

/** How many offers get a funnel strip before the rest go to the table + CSV. */
const STRIP_LIMIT = 8;

/**
 * The window from the URL, or the default — never a value the picker cannot
 * show. The server clamps `?days` to 1-400 and would answer a 45-day report
 * quite happily; the Range control has no option for it, so it would render
 * blank and the operator would be reading a window nothing on the page names.
 */
export function windowFromUrl(raw: string | null): number {
  const n = Number(raw);
  return RANGES.some((r) => r.value === n) ? n : 30;
}

interface Props {
  /** Exactly one of the two. A location, or the whole customer. */
  premise?: PremiseConfig;
  companyId?: string;
  companyName?: string;
}

export default function OfferRevenuePage({
  premise,
  companyId,
  companyName,
}: Props) {
  const place: PlaceRef | undefined = premise
    ? { premiseId: premise.premiseId }
    : companyId
      ? { companyId }
      : undefined;
  const seesOffers = usePermission("offers", "view", place);
  const seesRequests = usePermission("guest-requests", "view", place);
  const mayRead = seesOffers && seesRequests;
  const { t } = useTranslation(["offers", "common"]);

  const [params, setParams] = useSearchParams();
  // In the URL so a revenue report is a link somebody can paste into an email —
  // the single most common thing anyone does with one of these. Validated
  // against the four windows on offer rather than trusted: a pasted `?days=45`
  // would leave the Range control with no matching option, so an operator would
  // read a report whose window is not written anywhere on it.
  const days = windowFromUrl(params.get("days"));
  const setDays = useCallback(
    (value: string) => {
      const next = new URLSearchParams(params);
      next.set("days", value);
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  const [data, setData] = useState<
    OfferFunnelSummary | ChainOfferFunnelSummary | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const premiseId = premise?.premiseId;

  useEffect(() => {
    if (!mayRead) return;
    if (!premiseId && !companyId) return;
    let cancelled = false;
    // The old report is DISCARDED before the new one is asked for. This
    // component stays mounted across a scope change and a range change, and a
    // page that kept the previous summary would draw Hasselbacken's revenue
    // under Maple's header — with an Export CSV button already pointing at
    // Maple, so the file and the figures would disagree. A spinner is the
    // honest state while the answer is unknown.
    setData(null);
    setError(null);
    setLoading(true);
    void (async () => {
      try {
        const summary = premiseId
          ? await foyerApi.offerFunnel(premiseId, days)
          : await foyerApi.chainOfferFunnel(companyId!, days);
        if (!cancelled) {
          setData(summary);
          setError(null);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : t("offerRevenuePage.loadFailed")
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [mayRead, premiseId, companyId, days, t]);

  if (!mayRead) return <NotYourReport />;

  const csvUrl = premiseId
    ? foyerApi.offerFunnelCsvUrl(premiseId, days)
    : companyId
      ? foyerApi.chainOfferFunnelCsvUrl(companyId, days)
      : "#";

  const totals = data?.totals;
  // Nothing shown and nothing asked for: the teaching state, not a wall of
  // confident zeros.
  const hasAnything =
    !!totals && totals.impressions + totals.requests + totals.opens > 0;

  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("offerRevenuePage.eyebrow")}
        title={t("offerRevenuePage.title")}
        subtitle={
          premise
            ? t("offerRevenuePage.subtitlePremise")
            : t("offerRevenuePage.subtitleChain", {
                company: companyName ?? t("offerRevenuePage.thisCustomer"),
              })
        }
        actions={
          <>
            <TextField
              select
              size="small"
              label={t("offerRevenuePage.range")}
              value={days}
              onChange={(e) => setDays(e.target.value)}
              data-testid="revenue-range"
              sx={{ minWidth: 170 }}
            >
              {RANGES.map((r) => (
                <MenuItem key={r.value} value={r.value}>
                  {t(r.key)}
                </MenuItem>
              ))}
            </TextField>
            {/* A plain link, not a fetch: the browser streams the file and
                carries the session cookie itself. */}
            <Button
              component="a"
              href={csvUrl}
              disabled={!data}
              startIcon={<DownloadOutlinedIcon />}
              variant="outlined"
              data-testid="revenue-export"
            >
              {t("offerRevenuePage.exportCsv")}
            </Button>
          </>
        }
      />

      {/* POS-8, §6: a revenue figure produced by an always-accepts provider must
          never be screenshotted without the caveat attached. One component, one
          server verdict — there is deliberately no second banner. */}
      <DemoPaymentsBanner />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} data-testid="revenue-error">
          {error}
        </Alert>
      )}
      {loading && !data && <CircularProgress size={24} />}

      {data && !hasAnything && <TeachingEmptyState />}

      {data && hasAnything && (
        <Stack spacing={3}>
          <HeadlineCard
            totals={data.totals}
            from={data.from}
            to={data.to}
            premiseCount={
              "premiseCount" in data ? data.premiseCount : undefined
            }
          />

          {"series" in data && (
            <SectionCard
              title={t("offerRevenuePage.trend.title")}
              caption={t("offerRevenuePage.trend.caption")}
            >
              <OfferRevenueTrend
                series={data.series}
                currency={primaryMoney(data.totals.revenue)?.currency ?? null}
              />
            </SectionCard>
          )}

          {"premises" in data && <PremiseRanking data={data} />}

          <SectionCard
            title={t("offerRevenuePage.byOffer.title")}
            caption={t("offerRevenuePage.byOffer.caption")}
            action={<FunnelLegend />}
          >
            <OfferStrips
              rows={data.offers}
              premiseCount={"premises" in data ? data.premiseCount : undefined}
            />
          </SectionCard>

          <SectionCard
            title={t("offerRevenuePage.everyNumber.title")}
            caption={revenueNote(t, "grain")}
            action={
              <Typography variant="meta" sx={{ color: tokens.inkFaint }}>
                {t("offerRevenuePage.everyNumber.matchesCsv")}
              </Typography>
            }
          >
            <OfferTable rows={data.offers} chain={"premises" in data} />
          </SectionCard>
        </Stack>
      )}
    </PageContainer>
  );
}

/* -------------------------------------------------------------------------- */
/* The headline                                                               */
/* -------------------------------------------------------------------------- */

/**
 * One number, and the four sentences that make it believable.
 *
 * Revenue and pipeline are two figures with two labels and no arithmetic
 * between them. Putting pipeline in the hero — or under it as "including" —
 * is how "we nearly made this" becomes "we made this" in a slide three weeks
 * later.
 */
function HeadlineCard({
  totals,
  from,
  to,
  premiseCount,
}: {
  totals: OfferFunnelTotals;
  from: string;
  to: string;
  premiseCount?: number;
}) {
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const earnedNothing = moneyIsEmpty(totals.revenue);
  return (
    <Card variant="outlined" sx={{ p: 3 }} data-testid="revenue-headline">
      <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
        {premiseCount === undefined
          ? t("offerRevenuePage.headline.screenEarned", { from, to })
          : t("offerRevenuePage.headline.hotelsEarned", {
              count: premiseCount,
              from,
              to,
            })}
      </Typography>
      <Typography
        sx={{
          fontSize: 48,
          lineHeight: 1.1,
          fontWeight: "fontWeightBold",
          color: tokens.ink,
          mt: 0.5,
        }}
        data-testid="revenue-total"
      >
        {earnedNothing ? "0" : formatMoneyTotals(fmt, totals.revenue)}
      </Typography>
      <Typography variant="bodySm" sx={{ color: tokens.inkMuted, mt: 0.5 }}>
        {earnedNothing
          ? t("offerRevenuePage.headline.nothingAccepted")
          : revenueNote(t, "revenueRule")}
      </Typography>

      <Stack
        direction="row"
        spacing={5}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 3 }}
      >
        <Figure
          label={t("offerRevenuePage.figures.pipeline")}
          value={
            moneyIsEmpty(totals.pipeline)
              ? "0"
              : formatMoneyTotals(fmt, totals.pipeline)
          }
          hint={revenueNote(t, "pipeline")}
          testId="revenue-pipeline"
        />
        <Figure
          label={t("offerRevenuePage.figures.perMille")}
          value={formatPerMille(
            fmt,
            totals.revenuePerMille,
            totals.revenuePerMilleCurrency
          )}
          hint={perMilleHint(t, totals.revenuePerMille)}
          testId="revenue-per-mille"
        />
        <Figure
          label={t("offerRevenuePage.figures.impressions")}
          value={fmt.number(totals.impressions)}
          hint={t("offerRevenuePage.figures.impressionsHint")}
        />
        <Figure
          label={t("offerRevenuePage.figures.engaged", {
            rate: formatPercent(totals.engagedRate, fmt),
          })}
          value={fmt.number(totals.engaged)}
          hint={revenueNote(t, "engagedProxy")}
          testId="revenue-engaged"
        />
        <Figure
          label={t("offerRevenuePage.figures.requested")}
          value={fmt.number(totals.requests)}
          hint={t("offerRevenuePage.figures.requestedHint")}
        />
        <Figure
          label={t("offerRevenuePage.figures.accepted")}
          value={fmt.number(totals.accepted)}
          hint={t("offerRevenuePage.figures.acceptedHint")}
        />
        <Figure
          label={t("offerRevenuePage.figures.lost")}
          value={fmt.number(
            totals.declined + totals.cancelled + totals.refunded
          )}
          hint={t("offerRevenuePage.figures.lostHint")}
          testId="revenue-lost"
        />
      </Stack>
    </Card>
  );
}

function Figure({
  label,
  value,
  hint,
  testId,
}: {
  label: string;
  value: string;
  hint?: string;
  testId?: string;
}) {
  const body = (
    <Box>
      <Typography
        sx={{
          typography: "headline",
          fontWeight: "fontWeightBold",
          color: tokens.ink,
        }}
        data-testid={testId}
      >
        {value}
      </Typography>
      <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
        {label}
      </Typography>
    </Box>
  );
  return hint ? (
    <Tooltip title={hint} placement="bottom-start">
      {body}
    </Tooltip>
  ) : (
    body
  );
}

/* -------------------------------------------------------------------------- */
/* Offer rows                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * How many properties contributed to a chain row, or null on a premise row.
 *
 * `ChainOfferFunnelRow extends OfferFunnelRow`, so both views hand these
 * components the wider type and this is the one place that asks whether the
 * extra field is there. Narrowing with `in` inside JSX types the field as
 * `unknown`, which is how a perfectly good number ends up unrenderable.
 */
function premiseCountOf(row: OfferFunnelRow): number | null {
  const value = (row as Partial<ChainOfferFunnelRow>).premiseCount;
  return typeof value === "number" ? value : null;
}

function OfferStrips({
  rows,
  premiseCount,
}: {
  rows: OfferFunnelRow[];
  premiseCount?: number;
}) {
  const { t } = useTranslation("offers");
  const shown = rows.slice(0, STRIP_LIMIT);
  // Every per-1000 bar is drawn against the best one, so the tail stays
  // readable instead of collapsing into a row of invisible slivers.
  const bestPerMille = Math.max(0, ...rows.map((r) => r.revenuePerMille ?? 0));

  if (rows.length === 0) {
    return (
      <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
        {t("offerRevenuePage.strips.empty")}
      </Typography>
    );
  }

  return (
    <Stack
      spacing={3}
      divider={<Box sx={{ borderTop: `1px solid ${tokens.divider}` }} />}
    >
      {shown.map((row) => (
        <Box key={row.offerId}>
          <Stack
            direction="row"
            spacing={2}
            alignItems="baseline"
            flexWrap="wrap"
            useFlexGap
            sx={{ mb: 1.5 }}
          >
            <Typography
              sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}
            >
              {row.label}
            </Typography>
            {row.scope && row.scope !== "premise" && (
              <Chip
                size="small"
                label={
                  row.scope === "company"
                    ? t("offerRevenuePage.strips.chainOffer")
                    : t("offerRevenuePage.strips.globalOffer")
                }
                sx={{ height: 20, typography: "micro" }}
              />
            )}
            <ReachNote
              reached={premiseCountOf(row)}
              premiseCount={premiseCount}
            />
            <Box sx={{ flex: 1 }} />
            <PerMilleBar
              value={row.revenuePerMille}
              currency={row.revenuePerMilleCurrency}
              best={bestPerMille}
              offerId={row.offerId}
            />
          </Stack>
          <OfferFunnelStrip row={row} />
        </Box>
      ))}
      {rows.length > shown.length && (
        <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
          {t("offerRevenuePage.strips.more", {
            count: rows.length - shown.length,
          })}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * "3 of 4 hotels contributed" — how far a chain offer actually reached.
 *
 * A chain-scope offer is authored once and read by every hotel, so its row is
 * the sum of the hotels that produced anything. Without this line a campaign
 * that only one property ever showed reads identically to one the whole group
 * ran, which is exactly the comparison a group bought the chain view to make.
 */
function ReachNote({
  reached,
  premiseCount,
}: {
  reached: number | null;
  premiseCount?: number;
}) {
  const { t } = useTranslation("offers");
  if (reached === null) return null;
  const total = premiseCount ?? reached;
  return (
    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
      {t("offerRevenuePage.strips.contributed", { reached, count: total })}
    </Typography>
  );
}

/**
 * Revenue per 1000 impressions, as a number and as a bar against the best row.
 *
 * The three states are three different things and are drawn as three different
 * things: a value, an honest `0` (shown, earned nothing — the row an operator
 * is hunting for), and `—` for the two cases where the ratio is genuinely
 * unknowable. A `0` rendered as a dash would hide the worst performer in the
 * report, which is the one bug this column exists to avoid.
 */
function PerMilleBar({
  value,
  currency,
  best,
  offerId,
}: {
  value: number | null;
  currency: string | null;
  best: number;
  offerId: string;
}) {
  const { t } = useTranslation("offers");
  const fmt = useFormat();
  return (
    <Tooltip title={perMilleHint(t, value)} placement="top-end">
      <Box sx={{ minWidth: 190 }}>
        <Stack direction="row" spacing={1} alignItems="baseline">
          <Typography variant="micro" sx={{ color: tokens.inkMuted }}>
            {t("offerRevenuePage.strips.perMilleCaption")}
          </Typography>
          <Box sx={{ flex: 1 }} />
          <Typography
            variant="body2"
            sx={{
              fontWeight: "fontWeightBold",
              color: value === null ? tokens.inkFaint : tokens.ink,
              fontVariantNumeric: "tabular-nums",
            }}
            data-testid={`per-mille-${offerId}`}
          >
            {formatPerMille(fmt, value, currency)}
          </Typography>
        </Stack>
        <Box
          sx={{
            height: 6,
            mt: 0.5,
            borderRadius: "2px",
            bgcolor: tokens.rowAlt,
            overflow: "hidden",
          }}
        >
          {value !== null && best > 0 && (
            <Box
              sx={{
                height: "100%",
                width: `${(value / best) * 100}%`,
                bgcolor: tokens.vizMoney,
                borderRadius: "0 4px 4px 0",
              }}
            />
          )}
        </Box>
      </Box>
    </Tooltip>
  );
}

/**
 * The table view — every figure the charts round away, in one grid.
 *
 * Not a duplicate of the strips: a chart is read for shape and a table is read
 * for a number somebody is about to put in a spreadsheet, and the accessibility
 * contract for any colour-encoded chart is that the same data exists as text.
 * This is the shape the CSV serialises, column for column.
 */
function OfferTable({
  rows,
  chain,
}: {
  rows: OfferFunnelRow[];
  chain: boolean;
}) {
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  return (
    <TableContainer>
      <Table size="small" data-testid="revenue-offer-table">
        <TableHead>
          <TableRow>
            <TableCell>{t("offerRevenuePage.table.offer")}</TableCell>
            {chain && (
              <TableCell align="right">
                {t("offerRevenuePage.table.hotels")}
              </TableCell>
            )}
            <TableCell align="right">
              {t("offerRevenuePage.table.impressions")}
            </TableCell>
            <TableCell align="right">
              <Tooltip title={revenueNote(t, "engagedProxy")}>
                <span>{t("offerRevenuePage.table.engaged")}</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              {t("offerRevenuePage.table.opened")}
            </TableCell>
            <TableCell align="right">
              {t("offerRevenuePage.table.requested")}
            </TableCell>
            <TableCell align="right">
              {t("offerRevenuePage.table.accepted")}
            </TableCell>
            <TableCell align="right">
              {t("offerRevenuePage.table.declined")}
            </TableCell>
            <TableCell align="right">
              {t("offerRevenuePage.table.pending")}
            </TableCell>
            <TableCell align="right">
              {t("offerRevenuePage.table.refunded")}
            </TableCell>
            <TableCell align="right">
              {t("offerRevenuePage.table.revenue")}
            </TableCell>
            <TableCell align="right">
              <Tooltip title={t("offerRevenuePage.table.pipelineHint")}>
                <span>{t("offerRevenuePage.table.pipeline")}</span>
              </Tooltip>
            </TableCell>
            <TableCell align="right">
              {t("offerRevenuePage.table.perThousand")}
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.offerId} data-testid={`row-${row.offerId}`}>
              <TableCell>{row.label}</TableCell>
              {chain && (
                <TableCell align="right">{premiseCountOf(row) ?? ""}</TableCell>
              )}
              <Num value={fmt.number(row.impressions)} />
              <Num
                value={t("offerRevenuePage.table.engagedCell", {
                  count: fmt.number(row.engaged),
                  rate: formatPercent(row.engagedRate, fmt),
                })}
              />
              <Num value={fmt.number(row.opens)} />
              <Num value={fmt.number(row.requests)} />
              <Num value={fmt.number(row.accepted)} />
              <Num value={fmt.number(row.declined)} />
              <Num value={fmt.number(row.pending)} />
              <Num value={fmt.number(row.refunded)} />
              <Num
                value={
                  moneyIsEmpty(row.revenue)
                    ? "0"
                    : formatMoneyTotals(fmt, row.revenue)
                }
                strong
              />
              <Num
                value={
                  moneyIsEmpty(row.pipeline)
                    ? "0"
                    : formatMoneyTotals(fmt, row.pipeline)
                }
              />
              <Num
                value={formatPerMille(
                  fmt,
                  row.revenuePerMille,
                  row.revenuePerMilleCurrency
                )}
                muted={row.revenuePerMille === null}
              />
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function Num({
  value,
  strong,
  muted,
}: {
  value: string;
  strong?: boolean;
  muted?: boolean;
}) {
  return (
    <TableCell
      align="right"
      sx={{
        fontVariantNumeric: "tabular-nums",
        fontWeight: strong ? "fontWeightBold" : undefined,
        color: muted ? tokens.inkFaint : undefined,
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </TableCell>
  );
}

/* -------------------------------------------------------------------------- */
/* The chain layer                                                            */
/* -------------------------------------------------------------------------- */

/**
 * One row per hotel, best-earning first.
 *
 * Ranked on the largest single-currency revenue subtotal, re-asserted here so a
 * caller that hands the page an unsorted list still answers the question a
 * group actually asks: which of my properties is getting value out of this, and
 * what are the good ones doing differently.
 *
 * **The comparator is character-for-character the server's**
 * (`offerFunnelReport.chainOfferFunnel`), and it has to stay that way. Ties on
 * revenue are the common case rather than the exotic one — every property that
 * earned nothing is tied at zero — so a different tie-break here would order
 * the screen one way and the CSV (which serialises the server's order) another,
 * off the same data, in the same window. Requests before impressions, so a
 * hotel whose offers are free still ranks by how much they are used.
 */
function PremiseRanking({ data }: { data: ChainOfferFunnelSummary }) {
  const { t } = useTranslation(["offers", "common"]);
  const fmt = useFormat();
  const ranked = [...data.premises].sort(
    (a, b) =>
      (primaryMoney(b.totals.revenue)?.amount ?? 0) -
        (primaryMoney(a.totals.revenue)?.amount ?? 0) ||
      b.totals.requests - a.totals.requests ||
      b.totals.impressions - a.totals.impressions ||
      a.premiseName.localeCompare(b.premiseName)
  );
  return (
    <SectionCard
      title={t("offerRevenuePage.ranking.title")}
      caption={t("offerRevenuePage.ranking.caption")}
    >
      {ranked.length === 0 ? (
        <Typography variant="bodySm" sx={{ color: tokens.inkMuted }}>
          {t("offerRevenuePage.ranking.empty")}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" data-testid="revenue-premise-table">
            <TableHead>
              <TableRow>
                <TableCell>{t("offerRevenuePage.ranking.hotel")}</TableCell>
                <TableCell align="right">
                  {t("offerRevenuePage.table.revenue")}
                </TableCell>
                <TableCell align="right">
                  {t("offerRevenuePage.table.pipeline")}
                </TableCell>
                <TableCell align="right">
                  {t("offerRevenuePage.table.perThousand")}
                </TableCell>
                <TableCell align="right">
                  {t("offerRevenuePage.table.impressions")}
                </TableCell>
                <TableCell align="right">
                  {t("offerRevenuePage.table.requested")}
                </TableCell>
                <TableCell align="right">
                  {t("offerRevenuePage.table.accepted")}
                </TableCell>
                <TableCell>
                  {t("offerRevenuePage.ranking.bestEarningOffer")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ranked.map((p) => (
                <TableRow
                  key={p.premiseId}
                  data-testid={`hotel-${p.premiseId}`}
                >
                  <TableCell>{p.premiseName}</TableCell>
                  <Num
                    value={
                      moneyIsEmpty(p.totals.revenue)
                        ? "0"
                        : formatMoneyTotals(fmt, p.totals.revenue)
                    }
                    strong
                  />
                  <Num
                    value={
                      moneyIsEmpty(p.totals.pipeline)
                        ? "0"
                        : formatMoneyTotals(fmt, p.totals.pipeline)
                    }
                  />
                  <Num
                    value={formatPerMille(
                      fmt,
                      p.totals.revenuePerMille,
                      p.totals.revenuePerMilleCurrency
                    )}
                    muted={p.totals.revenuePerMille === null}
                  />
                  <Num value={fmt.number(p.totals.impressions)} />
                  <Num value={fmt.number(p.totals.requests)} />
                  <Num value={fmt.number(p.totals.accepted)} />
                  <TableCell>
                    {p.topOffer ? (
                      p.topOffer.label
                    ) : (
                      <Typography
                        variant="bodySm"
                        component="span"
                        sx={{ color: tokens.inkFaint }}
                      >
                        {t("common:labels.emptyValue")}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </SectionCard>
  );
}

/* -------------------------------------------------------------------------- */
/* Shell pieces                                                               */
/* -------------------------------------------------------------------------- */

function SectionCard({
  title,
  caption,
  action,
  children,
}: {
  title: string;
  caption?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="flex-start"
        spacing={2}
        sx={{ mb: caption ? 0.5 : 2 }}
      >
        <Typography sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}>
          {title}
        </Typography>
        {action}
      </Stack>
      {caption && (
        <Typography variant="meta" sx={{ color: tokens.inkMuted, mb: 2.5 }}>
          {caption}
        </Typography>
      )}
      {children}
    </Card>
  );
}

/**
 * The state this page will be in more often than any other in the first year.
 *
 * It teaches, in the register POS-5's Offers pane set: say what each thing
 * means in one sentence each, and say what the page will show once there is
 * something to show — rather than drawing a blank chart, which reads as a
 * broken feature and is the single fastest way to lose a hotel's interest in
 * the first week.
 */
function TeachingEmptyState() {
  const { t } = useTranslation("offers");
  return (
    <Card variant="outlined" sx={{ p: 4 }} data-testid="revenue-empty">
      <Stack spacing={1.5} sx={{ maxWidth: 640 }}>
        <Typography
          sx={{
            fontWeight: "fontWeightBold",
            color: tokens.ink,
            typography: "title",
          }}
        >
          {t("offerRevenuePage.teaching.title")}
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          <Trans
            t={t}
            i18nKey="offerRevenuePage.teaching.intro"
            components={{ strong: <strong /> }}
          />
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          {t("offerRevenuePage.teaching.rule")}
        </Typography>
        <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
          {t("offerRevenuePage.teaching.fills")}
        </Typography>
        <Box>
          <Button
            component={RouterLink}
            to="/frontlayer/offers"
            variant="contained"
            size="small"
            data-testid="revenue-empty-cta"
          >
            {t("offerRevenuePage.teaching.cta")}
          </Button>
        </Box>
      </Stack>
    </Card>
  );
}

/**
 * A direct link to a report somebody may not read.
 *
 * The nav hides the section already, so this is only ever a bookmark or a
 * pasted URL — and it names the missing right rather than showing an empty
 * page, because an empty report and a forbidden one look identical and only one
 * of them is worth telling somebody about.
 */
function NotYourReport() {
  const { t } = useTranslation("offers");
  return (
    <PageContainer>
      <PageHeader
        eyebrow={t("offerRevenuePage.eyebrow")}
        title={t("offerRevenuePage.title")}
      />
      <Card variant="outlined" sx={{ p: 4 }}>
        <Stack spacing={1} alignItems="flex-start">
          <Typography sx={{ fontWeight: "fontWeightBold", color: tokens.ink }}>
            {t("offerRevenuePage.notYourReport")}
          </Typography>
          <Typography variant="body2" sx={{ color: tokens.inkMuted }}>
            <Trans
              t={t}
              i18nKey="offerRevenuePage.notYourReportHelp"
              components={{ strong: <strong /> }}
            />
          </Typography>
        </Stack>
      </Card>
    </PageContainer>
  );
}
